import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { RemoveCircleOutline } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useIntl } from 'react-intl';
import DefectCard from './defectCard';
import './InspectionZones.scss';

const InspectionZones = ({ assetData, defectsOnly }) => {
	const { messages } = useIntl();

	if (!assetData || assetData.length === 0) {
		return null;
	}

	const zoneHasDefects = (zone) => {
		return zone.inspectionComponents?.some(component =>
			component.inspectionDefects && component.inspectionDefects.length > 0
		);
	};

	const componentHasDefects = (component) => {
		return component.inspectionDefects && component.inspectionDefects.length > 0;
	};

	// Check if asset has any defects
	const assetHasDefects = (asset) => {
		const zones = asset.inspectionZones || [];
		return zones.some(zoneHasDefects);
	};

	return (
		<Box className="inspection-zones-section">
			{assetData.map((asset) => {
				const zones = asset.inspectionZones || [];
				const hasDefects = assetHasDefects(asset);

				const visibleZones = defectsOnly
					? zones.filter(zoneHasDefects)
					: zones;

				return (
					<Box key={asset.assetId} className="asset-zones">
						<Typography variant="h3" className="asset-type-header">
							{asset.zoneLayoutName || asset.languageLabel || asset.assetName}
						</Typography>

						{/* Show no defects card when in defectsOnly mode and asset has no defects */}
						{defectsOnly && !hasDefects ? (
							<Box className="no-defects-card">
								<Typography variant="body1">
									{messages.noDefectsAssociated || 'There currently are no defects associated with this asset.'}
								</Typography>
								<CheckCircleIcon className="no-defects-check-icon" />
							</Box>
						) : visibleZones.length === 0 ? (
							<Box className="no-defects-empty-state">
								<Typography variant="body1" color="textSecondary">
									{messages.noDefectsInInspection || 'No defects found in this inspection'}
								</Typography>
							</Box>
						) : (
							visibleZones.map((zone, zoneIndex) => {
								const components = zone.inspectionComponents || [];

								const visibleComponents = defectsOnly
									? components.filter(componentHasDefects)
									: components;

								return (
									<Accordion
										key={`${zone.zoneId}-${zoneIndex}`}
										className="zone-accordion"
										defaultExpanded={true}
									>
										<AccordionSummary
											expandIcon={<ExpandMoreIcon />}
											className="zone-header"
										>
											<Box className="zone-title-container">
												<Typography variant="h3" className="zone-name">
													{zone.zoneName}
													{zone.incomplete && (
														<Tooltip title={messages['evirPx_Incomplete_Inspection'] || 'Incomplete Inspection'}>
															<RemoveCircleOutline className='incomplete-inspection-icon' />
														</Tooltip>
													)}
												</Typography>
												{zone.telemetry && (
													<Box className="zone-telemetry">
														{zone.telemetry.timestamp && (
															<span className="telemetry-item">
																{new Date(zone.telemetry.timestamp).toLocaleString()}
															</span>
														)}
														{zone.telemetry.odometer && (
															<span className="telemetry-item">
																{messages.odometer || 'Odometer'}: {zone.telemetry.odometer}
															</span>
														)}
													</Box>
												)}
											</Box>
										</AccordionSummary>

										<AccordionDetails className="zone-content">
											{visibleComponents.length > 0 ? (
												visibleComponents.map((component, componentIndex) => {
													const defects = component.inspectionDefects || [];

													return (
														<Box
															key={`${component.componentId}-${componentIndex}`}
															className="component-section"
														>
															<Typography variant="h4" className="component-name">
																{component.componentName}
															</Typography>

															{defects.length > 0 ? (
																<Box className="defects-list">
																	{defects.map((defect, defectIndex) => (
																		<DefectCard
																			key={`${defect.defectId}-${defectIndex}`}
																			defect={defect}
																		/>
																	))}
																</Box>
															) : (
																!defectsOnly && (
																	<Typography className="no-defects-message">
																		{messages.noDefectsFound || 'No defects found'}
																	</Typography>
																)
															)}
														</Box>
													);
												})
											) : (
												<Typography className="no-components-message">
													{messages.noComponentsInZone || 'No components in this zone'}
												</Typography>
											)}
										</AccordionDetails>
									</Accordion>
								);
							})
						)}
					</Box>
				);
			})}
		</Box>
	);
};

export default InspectionZones;
